/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import { useHistory } from 'react-router-dom'

import { Button } from '../Button'
import { font, weightRegular, weightSemiBold } from '../css'

interface ErrorScreenProps {
  message: string
  title?: string
  buttonText?: string
  onButtonPress?: () => void
  hasBigHeader?: boolean
}

const ErrorScreen = ({
  message,
  title = 'Oops!',
  buttonText = 'GO HOME',
  onButtonPress,
  hasBigHeader = false,
}: ErrorScreenProps) => {
  'use memo'

  const history = useHistory()
  const handleClick = onButtonPress ?? (() => history.push('/'))

  return (
    <Wrapper hasBigHeader={hasBigHeader}>
      <Card>
        <Image src='/images/unavailable_warning.png' alt='' />
        <Title>{title}</Title>
        <Body>{message}</Body>
        <Divider />
        <Button
          variant='primary'
          onClick={handleClick}
          label={buttonText}
          customCss={buttonCss}
        />
      </Card>
    </Wrapper>
  )
}

export default ErrorScreen

const Wrapper = styled.div<{ hasBigHeader: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({ hasBigHeader }) => (hasBigHeader ? '70vh' : '90vh')};
  padding: 24px 16px;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 480px;
  padding: 40px 24px;
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 16px;
`

const Image = styled.img`
  width: 126px;
  height: 96px;
  object-fit: contain;
`

const Title = styled.p`
  ${font.title}
  ${weightSemiBold}
  font-size: 40px;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${colors.WHITE};
  text-align: center;
  width: 100%;
  margin: 0;
`

const Body = styled.p`
  ${font.body}
  ${weightRegular}
  font-size: 18px;
  line-height: 1.45;
  color: ${colors.WHITE};
  text-align: center;
  width: 100%;
  margin: 0;
  white-space: pre-line;
  word-break: break-word;

  &::first-letter {
    text-transform: uppercase;
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.SECONDARY_300};
`

const buttonCss = css`
  height: 44px;
  border-radius: 8px;
  font-size: 20px;
  line-height: 24px;
  text-transform: uppercase;
`
